/**
 * ### convertHtmlToMarkdown
 *
 * @description
 * Converts HTML tags back to markdown syntax and handles HTML entities
 * Sanitizes input HTML to prevent XSS attacks before processing
 *
 * @example
 * const markdown = convertHtmlToMarkdown('This is <strong>bold</strong> and <em>italic</em> text');
 * // Returns: 'This is *bold* and _italic_ text'
 *
 * @param html - The HTML string to convert
 * @returns Plain text with markdown syntax
 */
const convertHTMLToMarkdown = (html: string): string => {
  // Create a temporary element to handle HTML entity decoding
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Convert any and all mention badges to text "{value}" format
  const badges = tempDiv.querySelectorAll('.mention-badge[data-mention-label][data-mention-value]');

  badges.forEach((badge) => {
    const value = badge.getAttribute('data-mention-value');

    if (value) {
      // Replace badge with {value} format as a text node to preserve surrounding HTML
      const mentionText = document.createTextNode(`{${value}}`);
      badge.parentNode?.replaceChild(mentionText, badge);
    }
  });

  // Helper function to process nested tags
  const processNestedTags = (
    outerSelector: string,
    innerSelector: string,
    markdownSymbol: string,
  ) => {
    const elements = tempDiv.querySelectorAll(`${outerSelector} ${innerSelector}`);
    elements.forEach((el) => {
      const textContent = el.textContent || '';
      const replacement = `${markdownSymbol}${textContent}${markdownSymbol}`;
      el.outerHTML = replacement;
    });
  };

  // Process nested tags first (from innermost to outermost)
  processNestedTags('b', 'i', '_'); // <b><i> -> *_text_*
  processNestedTags('i', 'b', '*'); // <i><b> -> _*text*_

  // Then process remaining individual tags
  // Convert <b> tags to *bold*
  const bElements = tempDiv.querySelectorAll('b');
  bElements.forEach((el) => {
    const textContent = el.textContent || '';
    const replacement = `*${textContent}*`;
    el.outerHTML = replacement;
  });

  // Convert <i> tags to _italic_
  const iElements = tempDiv.querySelectorAll('i');
  iElements.forEach((el) => {
    const textContent = el.textContent || '';
    const replacement = `_${textContent}_`;
    el.outerHTML = replacement;
  });

  // Convert <br> tags to \n
  const brElements = tempDiv.querySelectorAll('br');
  brElements.forEach((el) => {
    el.outerHTML = '\n';
  });

  // Return the text content (automatically handles HTML entities)
  return tempDiv.textContent || '';
};

export default convertHTMLToMarkdown;
